/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { FontAwesomeIcon, HR, Heading, Stack, Text } from "components";
import { Button } from "components/index";
import { pushToDataLayer } from "utils/gtm";
import useRouting from "hooks/useRouting";
import useTryCatch from "hooks/useTryCatch";
import { approveInvitation, rejectInvitation } from "lib/invitationApi";
import moment from "moment/moment";
import { useState } from "react";
import toast from "react-hot-toast";
import useEventRouting from "hooks/useEventRouting";
import ROUTES from "routes";
import { useInvitation } from "state/InvitationContext";
import { useUI } from "state/UIContext";
import isObject from "utils/isObject";
import { AvailabilitySection, CompanionSection } from "./Components";
import FeedbackModal from "./Components/FeedbackModal";
import { useTranslation } from "react-i18next";

const RsvpDetails = ({ invitationDetails, setInvitationDetails }) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useUI();
  const { shouldEnterDonePage } = useRouting();
  const { navigateWithEvent } = useEventRouting();
  const [isLoading, setLoading] = useState(false);
  const { updateInvitation } = useInvitation();
  const { eventId } = useEventRouting();

  const [dateOptionsErrors, setDateOptionsErrors] = useState({});
  const handleRejectFeedback = useTryCatch(async (feedback) => {
    closeModal();
    const { ok, data } = await rejectInvitation(
      invitationDetails.id,
      feedback,
      eventId,
    );

    if (ok) {
      updateInvitation(data);
      navigateWithEvent(ROUTES.onboard.exit);
    }
  }, setLoading);

  const validateDateOptions = (invitationDetails) => {
    const payload = {
      status: true,
      message: "",
    };
    if (invitationDetails.selected_dates.length === 0) {
      payload.status = false;
      payload.message = t("RsvpDetails.validationAtLeastOneDate");
      return payload;
    }
    Object.entries(invitationDetails.selected_dates).every(
      ([title, date], i) => {
        if (!date) {
          payload.status = false;
          payload.message = t("RsvpDetails.validationStartEndDate", {
            title,
          });
          setDateOptionsErrors((prev) => ({
            ...prev,
            [title]: true,
          }));
          return false;
        }
        const { start, end } = date;
        if (start.length === 0 && end.length === 0) {
          setDateOptionsErrors((prev) => ({
            ...prev,
            [title]: false,
          }));
          return true;
        }
        const isValid = moment(start).isBefore(end);
        if (!isValid) {
          payload.status = false;
          payload.message = t("RsvpDetails.validationInvalidRange");
          setDateOptionsErrors((prev) => ({
            ...prev,
            [title]: true,
          }));
          return false;
        }
        setDateOptionsErrors((prev) => ({
          ...prev,
          [title]: false,
        }));
        return true;
      },
    );
    return payload;
  };

  const handleApprove = useTryCatch(async () => {
    pushToDataLayer({
      event: "attendance_confirm_click",
      screen_name: "attendance",
      journey_step: "attendance",
    });
    const c = invitationDetails.category;
    if (+c === 1) {
      const { status, message } = validateDateOptions(invitationDetails);
      if (!status) {
        return toast.error(message);
      }
    }
    let selectedDates = invitationDetails.selected_dates;
    if (isObject(invitationDetails.selected_dates)) {
      selectedDates = Object.values(invitationDetails.selected_dates).map(
        (date) => date.start + "," + date.end,
      );
    }
    setLoading(true);
    const { ok, data } = await approveInvitation(
      invitationDetails.id,
      selectedDates,
      eventId,
    );
    setLoading(false);
    if (ok) {
      updateInvitation(data);
      const goto = await shouldEnterDonePage();
      navigateWithEvent(goto);
    }
  }, setLoading);

  return (
    <Stack className="w-full -bg--theme-colors-surface-neutral-level02 border -border--theme-colors-border-neutral-11 -rounded-outer-container overflow-hidden px-6 py-10 md:px-[54px] space-y-10">
      {/* Event Title and Cover */}
      <div className="flex flex-col gap-y-5">
        <Heading
          className="-text--theme-colors-text-title-brand text-2xl"
          as="h2"
        >
          {invitationDetails.event_title}
        </Heading>
        {invitationDetails.cover_image && (
          <div className="w-full">
            <img
              src={invitationDetails.cover_image}
              alt={t("guestInfoPage.eventCoverAlt")}
              className="w-full h-64 md:h-80 object-cover -rounded-image-viewer"
            />
          </div>
        )}
      </div>

      {/* Event Details Container */}
      <div className="space-y-10">
        <AvailabilitySection
          setInvitationDetails={setInvitationDetails}
          invitationDetails={invitationDetails}
          level={invitationDetails.category}
          dateOptionsErrors={dateOptionsErrors}
        />

        {(invitationDetails.location_title ||
          invitationDetails.address ||
          invitationDetails.website) && (
          <>
            <div
              className={`grid grid-cols-1 gap-10 ${
                (invitationDetails.location_title ||
                  invitationDetails.address) &&
                invitationDetails.website
                  ? "md:grid-cols-2"
                  : ""
              }`}
            >
              {/* Location */}
              {(invitationDetails.location_title ||
                invitationDetails.address) && (
                <Stack className="space-y-3">
                  <Text className="font-bold text-xl -text--theme-colors-text-title-brand">
                    {t("common.labels.location")}
                  </Text>
                  <div className="flex items-start gap-x-2 -text--theme-colors-text-body">
                    <div className="-text--theme-colors-icon-primary px-1">
                      <FontAwesomeIcon name="fa-location-dot" size="xl" />
                    </div>
                    <div>
                      {invitationDetails.location_title && (
                        <Text className="text-base">
                          {invitationDetails.location_title}
                        </Text>
                      )}
                      {invitationDetails.address && (
                        <Text className="text-base">
                          {invitationDetails.address}
                        </Text>
                      )}
                    </div>
                  </div>
                </Stack>
              )}

              {/* Website */}
              {invitationDetails.website && (
                <Stack className="space-y-3">
                  <Text className="font-bold text-xl -text--theme-colors-text-title-brand">
                    {t("RsvpDetails.website")}
                  </Text>
                  <div className="flex items-center gap-x-2 -text--theme-colors-text-body">
                    <div className="-text--theme-colors-icon-primary px-1">
                      <FontAwesomeIcon name="fa-globe" size="xl" />
                    </div>
                    <a
                      href={invitationDetails.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-base hover:underline break-all"
                    >
                      {invitationDetails.website}
                    </a>
                  </div>
                </Stack>
              )}
            </div>
          </>
        )}

        {/* About the Event */}
        {invitationDetails.description && (
          <>
            <HR className="!-border--theme-colors-border-neutral-07" />
            <Stack className="space-y-4">
              <Text className="font-bold text-xl -text--theme-colors-text-title-brand">
                {t("RsvpDetails.aboutEvent")}
              </Text>
              <Text className="-text--theme-colors-text-body text-base leading-relaxed">
                {invitationDetails.description}
              </Text>
            </Stack>
          </>
        )}

        {/* Companion Section */}
        {invitationDetails.companions_number > 0 && (
          <>
            <HR className="!-border--theme-colors-border-neutral-07" />
            <CompanionSection
              companions={Array.from({
                length: invitationDetails.companions_number,
              })}
            />
          </>
        )}
      </div>
      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Button
          variant="secondary"
          size="xl"
          className="order-1 md:order-0"
          onClick={() => {
            pushToDataLayer({
              event: "attendance_decline_click",
              screen_name: "attendance",
              journey_step: "attendance",
            });
            openModal({
              content: <FeedbackModal handleFeedback={handleRejectFeedback} />,
            });
          }}
        >
          {t("RsvpDetails.regretsMSG")}
        </Button>
        <Button
          variant="primary"
          className="order-0 md:order-1"
          size="xl"
          onClick={handleApprove}
          disabled={disableConfirmation(invitationDetails)}
          isLoading={isLoading}
        >
          {t("RsvpDetails.confirmMSG")}
        </Button>
      </div>
    </Stack>
  );
};

export default RsvpDetails;

const hasMultipleProperties = (obj, ...props) => {
  if (!obj) return false;
  return props.every((prop) => obj.hasOwnProperty(prop));
};

const disableConfirmation = (invitationDetails) => {
  if (!invitationDetails) return true;
  if (Array.isArray(invitationDetails.selected_dates)) {
    return invitationDetails.selected_dates.length === 0;
  }
  if (isObject(invitationDetails.selected_dates)) {
    if (Object.values(invitationDetails.selected_dates).length === 0)
      return true;
    return !Object.values(invitationDetails.selected_dates).some((date) =>
      hasMultipleProperties(date, "start", "end"),
    );
  }
  return true;
};
